import { z } from 'zod';
import { BULK_MISSED_CHECKOUT_CAP, MIN_PAIR_MINUTES, MISSED_CHECKOUT_MAX_OPEN_MS } from './bulkMissedCheckout.schema.js';

/* ===========================================================================
 * MANY MISSING CHECK-INS AT ONCE (owner, 2026-10-30: "why no check in bulk
 * action is there?"). The twin of the bulk check-out, for the queue's days
 * that have a check-out and NO check-in.
 *
 * Each day is a MISSED_PUNCH_IN correction filed through the corrections
 * service — the single "Set check-in time" action's own path (`POST
 * /corrections`), so the rules, the auto-apply and the audit are the ones one
 * filing gets. A day is named by (employee, company day), and each carries its
 * OWN wall-clock time on its OWN date: one typed time lands on every date in
 * the company zone, or each day takes its own suggestion.
 *
 * Two calls, one rule. `preview` names each day's check-out and suggestion
 * (and why a day cannot be filed); the save re-judges every day, so a day
 * closed in between is reported, not filed twice. The time rule below is
 * shared, so the card and the save cannot disagree about who is skipped.
 * ======================================================================== */

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM');
const unique = <T extends { employeeId: string; date: string }>(list: readonly T[]): boolean =>
  new Set(list.map((d) => `${d.employeeId}|${d.date}`)).size === list.length;

const day = z.object({ employeeId: z.string().min(1), date: ymd });

export const MissedCheckInPreviewSchema = z.object({
  days: z.array(day).min(1).max(BULK_MISSED_CHECKOUT_CAP).refine(unique, 'Each day may appear once'),
});
export type MissedCheckInPreviewInput = z.infer<typeof MissedCheckInPreviewSchema>;

export const BulkMissedCheckInSchema = z.object({
  /** The corrections composer's own floor, so the bulk path files nothing it would refuse. */
  reason: z.string().trim().min(5).max(200),
  days: z
    .array(day.extend({ /** Wall clock on `date`, in the company's zone. */ time: hhmm }))
    .min(1)
    .max(BULK_MISSED_CHECKOUT_CAP)
    .refine(unique, 'Each day may appear once'),
});
export type BulkMissedCheckInInput = z.infer<typeof BulkMissedCheckInSchema>;

/** Why a day was left alone. */
export const MISSED_CHECKIN_SKIPS = [
  'NOT_FOUND', // a person outside the caller's scope
  'NOT_OPEN', // the day has a check-in now, or left the queue, since the list was read
  'OWN_RECORD', // the filer's own day: it waits for another approver (M-25)
  'PENDING_EXISTS', // a correction for that day is already waiting
  'NO_TIME', // no time for that day: none typed and no suggestion
  'AFTER_CHECK_OUT', // at or after the day's check-out
  'TOO_LONG', // the pair would be longer than the open-interval cap
  'TOO_SHORT', // under MIN_PAIR_MINUTES before the check-out: the day would stay open
  'IN_FUTURE',
  'REFUSED', // the corrections rules refused it; `detail` says why
] as const;
export type MissedCheckInSkip = (typeof MISSED_CHECKIN_SKIPS)[number];

/**
 * Whether a check-in at `at` may open a day whose check-out is `checkOutAt`,
 * or the reason it may not. The one rule the card and the save both apply.
 */
export function missedCheckInTimeSkip(
  at: Date,
  checkOutAt: string | Date,
  now: Date
): Extract<MissedCheckInSkip, 'AFTER_CHECK_OUT' | 'TOO_LONG' | 'TOO_SHORT' | 'IN_FUTURE'> | null {
  const out = typeof checkOutAt === 'string' ? Date.parse(checkOutAt) : checkOutAt.getTime();
  if (at.getTime() >= out) return 'AFTER_CHECK_OUT';
  if (at.getTime() > now.getTime()) return 'IN_FUTURE';
  if (out - at.getTime() >= MISSED_CHECKOUT_MAX_OPEN_MS) return 'TOO_LONG';
  if (out - at.getTime() < MIN_PAIR_MINUTES * 60_000) return 'TOO_SHORT';
  return null;
}
